/**
 * useMapState - Map display and mode state management
 *
 * Manages all map-related display states including:
 * - Site/System mode toggle
 * - Map layer mode (geotiff, google, leaflet, street)
 * - Polygon visibility
 * - Measurement display
 * - Established roof tracking
 */

import { useState, useCallback } from 'react';

export type MapMode = 'geotiff-only' | 'google-maps' | 'leaflet-satellite' | 'street-map';
export type SiteSystemMode = 'site' | 'system';

export interface MapState {
  // Mode states
  siteSystemMode: SiteSystemMode;
  localMapMode: MapMode;

  // Display toggles
  showAllPolygons: boolean;
  showMeasurements: boolean;

  // Roof tracking
  establishedRoofIds: Set<number>;
}

export interface MapStateActions {
  // Mode setters
  setSiteSystemMode: (mode: SiteSystemMode) => void;
  setLocalMapMode: (mode: MapMode) => void;

  // Display setters
  setShowAllPolygons: (show: boolean) => void;
  setShowMeasurements: (show: boolean) => void;

  // Roof tracking setters
  setEstablishedRoofIds: React.Dispatch<React.SetStateAction<Set<number>>>;

  // Convenience methods
  toggleSiteSystemMode: () => void;
  toggleShowAllPolygons: () => void;
  toggleShowMeasurements: () => void;
  addEstablishedRoof: (id: number) => void;
  removeEstablishedRoof: (id: number) => void;
  isRoofEstablished: (id: number) => boolean;
}

export function useMapState(): MapState & MapStateActions {
  // Mode states
  const [siteSystemMode, setSiteSystemMode] = useState<SiteSystemMode>('system');
  const [localMapMode, setLocalMapMode] = useState<MapMode>('geotiff-only');

  // Display toggles
  const [showAllPolygons, setShowAllPolygons] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);

  // Roof tracking
  const [establishedRoofIds, setEstablishedRoofIds] = useState<Set<number>>(new Set());

  // Convenience methods
  const toggleSiteSystemMode = useCallback(() => {
    setSiteSystemMode(prev => prev === 'site' ? 'system' : 'site');
  }, []);

  const toggleShowAllPolygons = useCallback(() => {
    setShowAllPolygons(prev => !prev);
  }, []);

  const toggleShowMeasurements = useCallback(() => {
    setShowMeasurements(prev => !prev);
  }, []);

  const addEstablishedRoof = useCallback((id: number) => {
    setEstablishedRoofIds(prev => new Set([...prev, id]));
  }, []);

  const removeEstablishedRoof = useCallback((id: number) => {
    setEstablishedRoofIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const isRoofEstablished = useCallback((id: number) => {
    return establishedRoofIds.has(id);
  }, [establishedRoofIds]);

  return {
    // State
    siteSystemMode,
    localMapMode,
    showAllPolygons,
    showMeasurements,
    establishedRoofIds,

    // Setters
    setSiteSystemMode,
    setLocalMapMode,
    setShowAllPolygons,
    setShowMeasurements,
    setEstablishedRoofIds,

    // Convenience methods
    toggleSiteSystemMode,
    toggleShowAllPolygons,
    toggleShowMeasurements,
    addEstablishedRoof,
    removeEstablishedRoof,
    isRoofEstablished,
  };
}

export default useMapState;
